import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import { DateInput } from '@/common/components/DateInput/DateInput'
import { ErrorMessage } from '@/common/components/ErrorMessage'
import GoBackButton from '@/common/components/GoBackButton'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import { MultipleDocumentUploader } from '@/common/components/MultipleDocumentUploader'
import Select from '@/common/components/Select'
import SelectController from '@/common/components/SelectController'
import TextField from '@/common/components/TextField'
import useBlobs from '@/common/hooks/queries/useBlobs'
import { useCities } from '@/common/hooks/queries/useCities'
import { useProducts } from '@/common/hooks/queries/useProducts'
import useDebounce from '@/common/hooks/useDebounce'
import { GeoTransformers } from '@/common/services/geoTransformers'
import { Mapper } from '@/common/services/mapper'
import { Option } from '@/common/types/option'
import { ProductData } from '@/common/types/product.type'
import { FormTransformers } from '@/common/utils/formTransformers'
import { useCreatePriceSurvey } from '@/features/priceSurvey/hooks/useCreatePriceSurvey'
import { priceSurveySchema, PriceSurveyValidationSchema } from '@/features/priceSurvey/schemas/create.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronsRightIcon, InfoIcon, SearchIcon, XIcon } from 'lucide-react'
import { useState } from 'react'
import { Controller, SubmitHandler, useFieldArray, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

const Create = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [searchCity, setSearchCity] = useDebounce('')
  const [searchProduct, setSearchProduct] = useDebounce('')

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const { data: cityOptions } = useCities({
    filters: {
      search: searchCity,
    },
    mapper: Mapper.mapWithTransformer({ transformer: GeoTransformers.optionsTransformer() }),
  })

  const { data: productOptions } = useProducts({
    filters: { search: searchProduct },
    mapper: Mapper.mapToOptions<ProductData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const { mutateAsync: uploadBlob, isPending: uploadBlobIsPending } = useBlobs()

  const {
    register,
    handleSubmit,
    control,
    setError,
    setValue,
    formState: { errors },
  } = useForm<PriceSurveyValidationSchema>({
    resolver: zodResolver(priceSurveySchema),
    defaultValues: {
      survey_conducted_at: '',
      geo_geoables_attributes: [],
      price_survey_products_attributes: [],
    },
  })

  const {
    fields: priceSurveyProductsAttributes,
    append: appendPriceSurveyProduct,
    remove: removePriceSurveyProduct,
  } = useFieldArray({
    control,
    name: 'price_survey_products_attributes',
  })

  const { mutate: createPriceSurvey, isPending: createPriceSurveyIsPending } = useCreatePriceSurvey({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['priceSurveys'] })

      navigate('/price-survey')
    },
    onError: (error) =>
      FormTransformers.errorsTransformer<PriceSurveyValidationSchema>({ setError, setMessages })(error),
  })

  const onSubmit: SubmitHandler<PriceSurveyValidationSchema> = (data) => createPriceSurvey(data)

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="text-white-800">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-x-2">
            <GoBackButton fallback='/price-survey' searchLastRouteFromFallback />

            <div className="flex items-center">
              <p className="text-white-400 font-normal">Pesquisa de preços</p>

              <ChevronsRightIcon strokeWidth={1} className="size-8 text-white-400" />

              <p>Criar pesquisa</p>
            </div>
          </div>

          <Button
            name="submit"
            type="submit"
            className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
            disabled={createPriceSurveyIsPending || uploadBlobIsPending}
            isLoading={createPriceSurveyIsPending}
          >
            Confirmar
          </Button>
        </div>

        <Box className="flex flex-col gap-y-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-y-auto h-[calc(100vh-128px)]">
          <p className="text-lg font-bold">Dados da pesquisa</p>

          <DateInput
            fieldName="survey_conducted_at"
            control={control}
            setValue={setValue}
            errors={errors}
            register={register}
            label="Data da pesquisa"
            markAsRequired
            disabled={createPriceSurveyIsPending}
          />

          <SelectController
            control={control}
            name="geo_geoables_attributes"
            label="Cidade"
            markAsRequired
            isMulti
            options={cityOptions}
            onInputChange={setSearchCity}
            isDisabled={createPriceSurveyIsPending}
            errorMessage={errors.geo_geoables_attributes?.message}
          />

          <Box className="flex flex-col gap-y-6 p-4">
            <div>
              <Select
                placeholder={
                  <div className="flex items-center gap-x-2 text-white-800">
                    <SearchIcon strokeWidth={1} />
                    <p className="text-sm">Pesquisar Produtos</p>
                  </div>
                }
                options={productOptions.filter(
                  (option) => !priceSurveyProductsAttributes?.some(({ product_id }) => product_id === option.value),
                )}
                onInputChange={setSearchProduct}
                value={null}
                onChange={(value) => {
                  appendPriceSurveyProduct({
                    product_id: (value as Option).value || '',
                    display_name: (value as Option).label || '',
                    price: '',
                  })
                }}
              />

              {errors?.price_survey_products_attributes?.message && (
                <ErrorMessage message={errors.price_survey_products_attributes.message} />
              )}
            </div>

            {priceSurveyProductsAttributes.map((field, index) => (
              <div key={field.product_id} className="flex items-start gap-x-2">
                <Button
                  variant="outline-text"
                  className="text-danger hover:text-danger-soft transition-colors p-0 mt-[2.0625rem]"
                  onClick={() => removePriceSurveyProduct(index)}
                >
                  <XIcon strokeWidth={1} className="size-6" />
                </Button>

                <TextField label="Produto" value={field.display_name} className="w-full" disabled />

                <Controller
                  control={control}
                  name={`price_survey_products_attributes.${index}.price`}
                  disabled={createPriceSurveyIsPending}
                  render={({ field: { onBlur, ...rest } }) => (
                    <CurrencyTextField
                      labelClass="text-white-800"
                      label="Valor"
                      className="w-full"
                      {...rest}
                      errorMessage={errors.price_survey_products_attributes?.[index]?.price?.message}
                    />
                  )}
                />
              </div>
            ))}
          </Box>

          <div className="flex gap-x-2 items-center my-2">
            <InfoIcon strokeWidth={1} className="text-yellow-600" />

            <div>
              <p className="text-[0.625rem] leading-3 font-bold">Regras da ANP</p>
              <p className="text-[0.625rem] leading-3 font-light italic">
                Na ausência de uma pesquisa informada, será utilizado o valor máximo da ANP correspondente à UF
              </p>
            </div>
          </div>

          <MultipleDocumentUploader
            title="Anexe documentos"
            control={control}
            name="evidences_attachments_attributes"
            uploadBlob={uploadBlob}
            fieldName="evidences_attachments_attributes"
            accept=".png, .jpg, .jpeg, .webp, .pdf, .doc, .docx, .xls, .xlsx"
            disabled={uploadBlobIsPending || createPriceSurveyIsPending}
          />
        </Box>
      </form>

      {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
    </>
  )
}

export default Create
